import dayjs from 'dayjs';

import {i18n} from '@yourcoach/i18n';

import type {
  ConferenceApiModel,
  ConferenceExpandedCommon,
} from '../../../api/conference';
import type {CourseApiModel as CourseBase} from '../../../api/course';
import type {EditionApiModel, ProgramApiModel} from '../../../api/program';
import type {BaseTaskApiModel, CoachTaskApiModel} from '../../../api/task';
import type {ApiResourceName, Expand, MaybeExpanded} from '../../../api/types';
import type {CoreCoachEditionsUpdateParams} from '../../../api/types/core/methods';
import type {RpcRequest} from '../../../api/utils/apiRequest';
import type {DateTimeObj, NaiveDateTimeObj} from '../../../utils/datetime';
import type {Nullable} from '../../../utils/utility-types';

import {conferenceStore} from '../../../api/conference';
import {editionStore} from '../../../api/program';
import {apiRequest} from '../../../api/utils/apiRequest';
import {expandObj} from '../../../api/utils/expand';
import {alert} from '../../../components/alert';
import {
  datetimeObjToISOString,
  getSecondsFromMidnight,
} from '../../../utils/datetime';
import {isCourseEvergreen} from '../../../utils/entity/course';
import {getEntityTypeById} from '../../../utils/entity/getEntityType';
import {
  EVERGREEN_PROGRAM_SCHEDULE_DURATION,
  isEditionIndividual,
} from '../../../utils/entity/program';
import {isBaseTask} from '../../../utils/entity/task';
import {envManager} from '../../../utils/env';
import {getUuid} from '../../../utils/getUuid';
import {COMMAND} from '../utils';

type ActionType = 'create' | 'delete' | 'update';

type ConferenceUpdate =
  Required<CoreCoachEditionsUpdateParams>['conferences'][number];

type EntityExpandedFields = {
  course: MaybeExpanded<
    CourseBase & {
      edition: MaybeExpanded<EditionApiModel>;
    }
  >;
};

type ExpandedEntity = (
  | BaseTaskApiModel
  | CoachTaskApiModel
  | ConferenceApiModel
) &
  EntityExpandedFields;

type TargetType = 'conference' | 'material' | 'task';

type TaskUpdate = Required<CoreCoachEditionsUpdateParams>['tasks'][number];

export async function updateProgramScheduleHook<
  Entity extends BaseTaskApiModel | CoachTaskApiModel | ConferenceApiModel,
>(entities: Entity[], actionType: ActionType) {
  if (envManager.isB2b) {
    return;
  }

  const firstEntity = entities[0]!;

  const entityType = getEntityTypeById(firstEntity._id);

  if (
    !(['conference', 'base_task', 'coach_task'] as ApiResourceName[]).includes(
      entityType!,
    )
  ) {
    throw `${entityType} is not supported.`;
  }

  if (firstEntity.program_id && firstEntity.course_id) {
    const {expandedEntities, program} = await fetchData(entities, actionType);

    // for programs created from an offer
    // we don't need to update the program's schedule
    if (program.offer_id) {
      return COMMAND.CANCEL;
    }

    const lastEdition = program.editions.slice(-1).pop();

    if (lastEdition) {
      let editionEntities:
        | CoreCoachEditionsUpdateParams['conferences']
        | CoreCoachEditionsUpdateParams['tasks'];
      let updateKey: 'conferences' | 'tasks';

      switch (entityType) {
        case 'base_task':
        case 'coach_task':
          editionEntities = [...lastEdition.tasks];
          updateKey = 'tasks';
          break;
        case 'conference':
          editionEntities = [...lastEdition.conferences];
          updateKey = 'conferences';
          break;
        default:
          return false;
      }

      expandedEntities.forEach(entity => {
        patchEditionEntities(editionEntities!, entity, actionType);
      });

      const areThereChanges =
        JSON.stringify(editionEntities) !==
        JSON.stringify(lastEdition[updateKey]);

      const shouldAskForUpdate = actionType === 'create' || areThereChanges;

      if (shouldAskForUpdate) {
        const shouldUpdate = await shouldUpdateProgramScheduleAlert({
          actionType,
          isIndividual: isEditionIndividual(lastEdition),
          programTitle: program.title,
          targetType: entityType!.includes('task')
            ? 'task'
            : (entityType as any),
        });

        if (shouldUpdate) {
          await editionStore.update(lastEdition, {
            revision: lastEdition.revision,
            [updateKey]: editionEntities,
          });

          if (actionType === 'create' && entityType === 'conference') {
            conferenceStore.coach.updateBatch(
              (expandedEntities as ConferenceExpandedCommon[]).map(
                conference => ({
                  entity: conference,
                  params: {
                    edition_uuid: conference.edition_uuid,
                  },
                }),
              ),
            );
          }

          return true;
        }
      }

      return COMMAND.CANCEL;
    } else {
      return COMMAND.CANCEL;
    }
  }
}

async function fetchData<
  Entity extends BaseTaskApiModel | CoachTaskApiModel | ConferenceApiModel,
>(entities: Entity[], actionType: ActionType) {
  const firstEntity = entities[0]!;

  const entityType = getEntityTypeById(firstEntity._id);

  const programExpand: Expand = {program: ['edition_ids']};
  const courseExpand: Expand['course'] = [
    ['course_id', null, {course: ['edition_id']}],
  ];

  const entityExpand: Record<string, Expand> = {
    base_task: {base_task: courseExpand},
    coach_task: {coach_task: courseExpand},
    conference: {conference: courseExpand},
  };

  const batch = Array.from<RpcRequest>([
    {
      method: 'coach.programs.read',
      params: {
        _id: firstEntity.program_id,
        expand: programExpand,
      },
    },
  ]);

  /**
   * If a base_task was deleted the backend doesn't return it or its coach tasks
   * so we continue to work with passed entities in delete mode.
   * In delete mode we just remove the entities, so we don't need additional data from them
   */
  if (actionType !== 'delete') {
    batch.push(
      ...Array.from<null | RpcRequest>([
        entityType === 'conference'
          ? {
              method: 'coach.conferences.list',
              params: {
                expand: entityExpand.conference,
                query: [['_id', 'in', entities.map(entity => entity._id)]],
              },
            }
          : null,
        entityType === 'base_task'
          ? {
              method: 'coach.tasks.base.list',
              params: {
                expand: entityExpand.base_task,
                query: [['_id', 'in', entities.map(entity => entity._id)]],
              },
            }
          : null,
        entityType === 'coach_task'
          ? {
              method: 'coach.tasks.coach.list',
              params: {
                expand: entityExpand.coach_task,
                query: [['_id', 'in', entities.map(entity => entity._id)]],
              },
            }
          : null,
      ]).filter(Boolean),
    );
  }

  const [programResult, entitiesList] = await apiRequest({
    batch,
  });

  const program = expandObj(programResult.program, {
    expand: programExpand,
    expanded: programResult._expanded,
  }) as ProgramApiModel & {editions: EditionApiModel[]};

  const expandedEntities: ExpandedEntity[] = entitiesList
    ? entitiesList._items.map(item =>
        expandObj(item, {
          expand: entityExpand[entityType!]!,
          expanded: entitiesList._expanded,
        }),
      )
    : entities;

  return {expandedEntities, program};
}

function getDayFromEntity(entity: ExpandedEntity, isDelete?: boolean): number {
  const day = 0;

  if (isDelete) {
    return day;
  }

  return getDayFromEntityDate(entity, entity.start_date);
}

function getDayFromEntityDate(
  entity: ExpandedEntity,
  date: DateTimeObj | NaiveDateTimeObj,
): number {
  let day = 0;

  if (entity.course) {
    const stopDate = dayjs(datetimeObjToISOString(date));

    const dayFromStart = stopDate.diff(
      dayjs(datetimeObjToISOString(entity.course.start_date)),
      'day',
    );
    const courseDuration = isCourseEvergreen(entity.course)
      ? EVERGREEN_PROGRAM_SCHEDULE_DURATION
      : (entity.course.edition?.duration ?? 0);

    if (courseDuration > 0) {
      day = Math.min(dayFromStart, courseDuration - 1);
    }
  }

  return day;
}

function getStopDayFromEntity(
  entity: BaseTaskApiModel & EntityExpandedFields,
  isDelete?: boolean,
): Nullable<number> {
  let day: Nullable<number> = null;

  if (isDelete) {
    return day;
  }

  if (entity.stop_date) {
    day = getDayFromEntityDate(entity, entity.stop_date);
  }

  return day;
}

function getTimeAndDurationFromEntity(
  entity: ExpandedEntity,
  isDelete?: boolean,
): {duration: number; time: number} {
  if (isDelete) {
    return {duration: 0, time: 0};
  }

  let duration = 0;
  let time = 0;

  const {end_date, start_date} = entity as ConferenceApiModel;

  const startDate = dayjs(datetimeObjToISOString(start_date));

  if ((entity as BaseTaskApiModel).duration) {
    duration = (entity as BaseTaskApiModel).duration;
  } else {
    const endDate = dayjs(datetimeObjToISOString(end_date));

    duration = endDate.diff(startDate, 'second');
  }

  time = getSecondsFromMidnight(startDate.toDate());

  return {duration, time};
}

function mapEntityToScheduleUpdate(
  entity: ExpandedEntity,
  isDelete?: boolean,
): ConferenceUpdate | TaskUpdate {
  const {description, title, uuid} = entity;

  type Patch = {
    coach_attachment_ids?: string[];
    day: number;
    description: string;
    duration: number;
    edition_uuid?: Nullable<string>;
    repeat?: TaskUpdate['repeat'];
    speaker_ids?: string[];
    stop_day?: TaskUpdate['stop_day'];
    time: number;
    title: string;
    uuid: string;
  };

  const day = getDayFromEntity(entity, isDelete);
  const {duration, time} = getTimeAndDurationFromEntity(entity, isDelete);
  const result: Patch = {
    day,
    description,
    duration,
    time,
    title,
    uuid,
  };

  if ((entity as ConferenceApiModel).speaker_ids) {
    result.speaker_ids = (entity as ConferenceApiModel).speaker_ids;

    (entity as ConferenceApiModel).edition_uuid =
      (entity as ConferenceApiModel).edition_uuid || getUuid();
    result.edition_uuid = (entity as ConferenceApiModel).edition_uuid;
  }

  if ((entity as CoachTaskApiModel).coach_attachment_ids) {
    result.coach_attachment_ids = (
      entity as CoachTaskApiModel
    ).coach_attachment_ids;

    if (isBaseTask(entity)) {
      result.repeat = entity.repeat;
      result.stop_day = getStopDayFromEntity(entity);
    }
  }

  return result;
}

function patchEditionEntities(
  editionEntities:
    | Required<CoreCoachEditionsUpdateParams>['conferences']
    | Required<CoreCoachEditionsUpdateParams>['tasks'],
  entity: ExpandedEntity,
  actionType: ActionType,
) {
  const entityType = getEntityTypeById(entity._id);

  const index = editionEntities.findIndex(c => {
    if (entityType?.includes('task')) {
      return c.uuid === entity.uuid;
    } else if (entityType === 'conference') {
      return c.edition_uuid === (entity as ConferenceApiModel).edition_uuid;
    }

    return false;
  });

  switch (actionType) {
    case 'create': {
      if (index === -1) {
        editionEntities.push(
          mapEntityToScheduleUpdate(entity) as ConferenceUpdate,
        );
      }
      break;
    }
    case 'delete': {
      if (index >= 0) {
        editionEntities.splice(index, 1);
      }
      break;
    }
    case 'update': {
      if (index >= 0) {
        editionEntities.splice(index, 1, mapEntityToScheduleUpdate(entity));
      }
      break;
    }
  }
}

async function shouldUpdateProgramScheduleAlert(args: {
  actionType: ActionType;
  isIndividual?: boolean;
  programTitle: string;
  targetType: TargetType;
}): Promise<boolean> {
  const {actionType, isIndividual = false, programTitle, targetType} = args;

  const groupType = isIndividual ? 'individual' : 'group';

  if (targetType === 'conference' && isIndividual) {
    return false;
  }

  return alert.confirm({
    message:
      /**
       * @i18n_parser
       * t('shared:services.entity_action.program.update_schedule_popup.description.conference.create.individual')
       * t('shared:services.entity_action.program.update_schedule_popup.description.conference.create.group')
       * t('shared:services.entity_action.program.update_schedule_popup.description.conference.delete.individual')
       * t('shared:services.entity_action.program.update_schedule_popup.description.conference.delete.group')
       * t('shared:services.entity_action.program.update_schedule_popup.description.conference.update.individual')
       * t('shared:services.entity_action.program.update_schedule_popup.description.conference.update.group')
       * t('shared:services.entity_action.program.update_schedule_popup.description.task.create.individual')
       * t('shared:services.entity_action.program.update_schedule_popup.description.task.create.group')
       * t('shared:services.entity_action.program.update_schedule_popup.description.task.delete.individual')
       * t('shared:services.entity_action.program.update_schedule_popup.description.task.delete.group')
       * t('shared:services.entity_action.program.update_schedule_popup.description.task.update.individual')
       * t('shared:services.entity_action.program.update_schedule_popup.description.task.update.group')
       * t('shared:services.entity_action.program.update_schedule_popup.description.material.create.individual')
       * t('shared:services.entity_action.program.update_schedule_popup.description.material.create.group')
       * t('shared:services.entity_action.program.update_schedule_popup.description.material.update.individual')
       * t('shared:services.entity_action.program.update_schedule_popup.description.material.update.group')
       * t('shared:services.entity_action.program.update_schedule_popup.description.material.delete.individual')
       * t('shared:services.entity_action.program.update_schedule_popup.description.material.delete.group')
       */
      i18n.t(
        `shared:services.entity_action.program.update_schedule_popup.description.${targetType}.${actionType}.${groupType}`,
        {
          programTitle,
        },
      ),
    title:
      /**
       * @i18n_parser
       * t('shared:services.entity_action.program.update_schedule_popup.title.conference.create')
       * t('shared:services.entity_action.program.update_schedule_popup.title.conference.update')
       * t('shared:services.entity_action.program.update_schedule_popup.title.conference.delete')
       * t('shared:services.entity_action.program.update_schedule_popup.title.task.create')
       * t('shared:services.entity_action.program.update_schedule_popup.title.task.update')
       * t('shared:services.entity_action.program.update_schedule_popup.title.task.delete')
       * t('shared:services.entity_action.program.update_schedule_popup.title.material.create')
       * t('shared:services.entity_action.program.update_schedule_popup.title.material.update')
       * t('shared:services.entity_action.program.update_schedule_popup.title.material.delete')
       */
      i18n.t(
        `shared:services.entity_action.program.update_schedule_popup.title.${targetType}.${actionType}`,
      ),
  });
}
